import React from "react";
import { resolveDisplayOrdinal, type OrdinalMap } from "@/lib/questionOrdinals";
import QuestionCard from "./QuestionCard";
import type { AssessmentQuestionRow } from "@/api/fetchAssessmentQuestions";
import { resolveServiceModelEvidenceForQuestion } from "@/configs/telecom/serviceModelQuestionEvidence";
type Domain = string;

type NormalizedOption = { label: string; score: number; description: string };

/** Known label → score mapping for all questionnaire types */
const LABEL_SCORE_MAP: Record<string, number> = {
  "Nonexistent": 0, "Initial": 25, "Developing": 50, "Defined/Managed": 75, "Optimized": 100,
  "Low Risk": 1, "Moderate Risk": 2, "High Risk": 3,
  "Low": 1, "Medium": 2, "High": 3, "Unknown": 9,
  "Small": 1, "Large": 3,
  "Yes": 1, "No": 2,
  "Active / Unresolved": 3, "Historical (Resolved)": 2, "None Known": 1,
};

const TRIAGE_CANONICAL_SCALES: Record<string, NormalizedOption[]> = {
  TPT_P1_COUNTRY: [
    { label: "North America", score: 1, description: "Primary market in the United States or Canada." },
    { label: "Europe", score: 2, description: "Primary market in the EU/EEA, UK, or Switzerland." },
    { label: "Latin America & Caribbean", score: 3, description: "Primary market in Latin America or the Caribbean." },
    { label: "Middle East & North Africa", score: 4, description: "Primary market in MENA region." },
    { label: "Sub-Saharan Africa", score: 5, description: "Primary market in Sub-Saharan Africa." },
    { label: "Asia-Pacific", score: 6, description: "Primary market in APAC region." },
    { label: "Other / Multi-Region", score: 7, description: "Multi-region or other geography." },
    { label: "Unknown", score: 9, description: "Primary market not confirmed." },
  ],
  TPT_P2_SUBSCRIBERS: [
    { label: "<100k", score: 1, description: "Fewer than 100,000 subscribers." },
    { label: "100k–1M", score: 2, description: "Between 100k and 1 million subscribers." },
    { label: "1M–10M", score: 3, description: "Between 1 and 10 million subscribers." },
    { label: ">10M", score: 4, description: "More than 10 million subscribers." },
    { label: "Unknown", score: 9, description: "Subscriber base size not confirmed." },
  ],
  TPT_P2_SCALE: [
    { label: "Small", score: 1, description: "Limited footprint — regional, niche, or low-volume operations." },
    { label: "Medium", score: 2, description: "Moderate scale across multiple regions, customers, or infrastructure assets." },
    { label: "Large", score: 3, description: "Large-scale operations across many regions, customers, or infrastructure assets." },
    { label: "Unknown", score: 9, description: "Scale of operations not confirmed." },
  ],
  TPT_P3_CNI: [
    { label: "Yes", score: 3, description: "Operator is designated as Critical National Infrastructure." },
    { label: "No", score: 1, description: "Operator is not designated as CNI." },
    { label: "Unknown", score: 9, description: "CNI designation not confirmed." },
  ],
};

/**
 * Normalize answer_scale from ANY DB shape into a consistent Option[].
 * Handles:
 *  - Record<string, string>  (label → description)
 *  - Array<{label, description?, value?, score?}>
 *  - null/undefined → undefined (use fallback scale)
 */
function normalizeAnswerScale(
  raw: any,
  orgLabel: string,
  questionKey?: string | null
): NormalizedOption[] | undefined {
  const canonical = questionKey ? TRIAGE_CANONICAL_SCALES[questionKey] : undefined;
  if (!raw) return canonical;

  const scoreForLabel = (label: string, idx: number, scoreMapping?: Record<string, unknown>) => {
    const mapped = scoreMapping?.[label];
    if (mapped != null && Number.isFinite(Number(mapped))) return Number(mapped);
    if (LABEL_SCORE_MAP[label] != null) return LABEL_SCORE_MAP[label];
    return idx + 1;
  };

  const fromLabels = (
    labels: unknown[],
    scoreMapping?: Record<string, unknown>,
    useKnownScores = true,
  ) =>
    labels.map((labelValue, idx) => {
      const label = String(labelValue);
      return {
        label,
        score: useKnownScores ? scoreForLabel(label, idx, scoreMapping) : idx + 1,
        description: label,
      };
    });

  const normalizeEntries = (source: any, scoreMapping?: Record<string, unknown>) => {
    if (Array.isArray(source)) {
      return source.map((item: any, idx: number) => {
        if (typeof item !== "object" || item === null) {
          const label = String(item);
          return { label, score: scoreForLabel(label, idx, scoreMapping), description: label };
        }
        const label = String(item.label ?? item.text ?? item.name ?? `Option ${idx + 1}`);
        const desc = String(item.description ?? item.desc ?? label);
        const score = item.value ?? item.score ?? scoreForLabel(label, idx, scoreMapping);
        return { label, score: Number(score), description: desc };
      });
    }

    if (source && typeof source === "object") {
      return Object.entries(source).map(([label, val], idx) => {
        const desc = typeof val === "string" ? val : (val as any)?.description ?? (val as any)?.desc ?? label;
        const explicitScore = typeof val === "object" && val !== null ? (val as any)?.score ?? (val as any)?.value : undefined;
        return {
          label,
          score: explicitScore != null ? Number(explicitScore) : scoreForLabel(label, idx, scoreMapping),
          description: String(desc),
        };
      });
    }

    return [];
  };

  let entries: NormalizedOption[] = [];

  const isBackendMetadata = raw && !Array.isArray(raw) && typeof raw === "object"
    && (raw.item_type || raw.input || raw.options_source || raw.scoring);

  if (canonical && isBackendMetadata) {
    entries = canonical;
  } else if (Array.isArray(raw)) {
    entries = normalizeEntries(raw);
  } else if (typeof raw === "object") {
    const scoreMapping = raw.score_mapping && typeof raw.score_mapping === "object"
      ? raw.score_mapping as Record<string, unknown>
      : undefined;

    if (Array.isArray(raw.options)) {
      entries = fromLabels(raw.options, scoreMapping);
    } else if (raw.options && typeof raw.options === "object") {
      entries = normalizeEntries(raw.options, scoreMapping);
    } else if (raw.options_source === "iso_country_list") {
      entries = canonical ?? [];
    } else {
      entries = normalizeEntries(raw, scoreMapping);
    }
  }

  if (entries.length === 0) return undefined;

  // Replace org tokens in descriptions
  return entries.map((e) => ({
    ...e,
    description: e.description
      .replace(/####/g, orgLabel)
      .replace(/\b[Tt]he [Oo]rganization\b/g, orgLabel)
      .replace(/\b[Tt]he company\b/g, orgLabel),
  }));
}

type Props = {
  domain: Domain;
  rows: AssessmentQuestionRow[];
  responsesById: Map<number, { score: number | null; comment: string | null; evidence_links?: string[] }>;
  onSave: (questionId: number, score: number | null, comment: string | null, evidenceLinks?: string[]) => Promise<void> | void;
  onEvidenceUpdate: (questionId: number, links: string[]) => Promise<void> | void;

  assessmentId: string;
  orgName?: string;
  questionnaireId?: string;
  readOnly?: boolean;
  /** Set of question_ids that failed evidence validation */
  evidenceErrorIds?: Set<number>;
  /** Global offset for continuous question numbering across domains */
  globalIndexOffset?: number;
  /** Phase 2A closeout — frozen per-assessment ordinals; preferred over positional numbering. */
  questionOrdinals?: OrdinalMap;
  /**
   * Phase 2.9.2 — Fallback inherited Telecom Service Model evidence groups
   * (used when no per-question controls are available).
   */
  serviceModelEvidenceGroups?: { model: string; items: string[] }[];
  /**
   * Phase 2.9.4 — Inherited Telecom Service Models from the parent portfolio.
   * When provided (telecom_v2 only), per-question Additional Evidence is
   * resolved from each question's NIST control(s) + selected service model(s).
   */
  serviceModels?: string[];
};

export function DomainSection({
  domain,
  rows,
  responsesById,
  onSave,
  onEvidenceUpdate,
  assessmentId,
  orgName,
  questionnaireId,
  readOnly = false,
  evidenceErrorIds,
  globalIndexOffset = 0,
  questionOrdinals,
  serviceModelEvidenceGroups,
  serviceModels,
}: Props) {
  // Fallback org label if none provided
  const orgLabel = (orgName && orgName.trim().length > 0) ? orgName : "your organization";

  return (
    <div className="space-y-6">
      {rows?.map((row, index) => {
        // pull current response for this question
        const resp = responsesById.get(row.question_id) ?? { score: null, comment: "" };
        const value = resp.score ?? null;
        const comment = (resp.comment ?? "") as string;
        const evidenceLinks = (resp.evidence_links ?? []) as string[];

        // 🔁 Replace #### / "the Organization" / "the company" with org name for display
        const renderedText = (row.question_text ?? "")
          .replace(/####/g, orgLabel)
          .replace(/\b[Tt]he [Oo]rganization\b/g, orgLabel)
          .replace(/\b[Tt]he company\b/g, orgLabel);

        // Normalize answer_scale from any DB shape into consistent Option[]
        const scaleOptions = normalizeAnswerScale(row.answer_scale, orgLabel, row.question_key);

        // Phase 2.9.4 — Resolve per-question Additional Evidence using the
        // question's NIST controls + inherited service models. Falls back to
        // the page-level groups when no models / no controls are present.
        const perQuestionGroups =
          serviceModels && serviceModels.length > 0
            ? resolveServiceModelEvidenceForQuestion(row.controls ?? [], serviceModels)
            : (serviceModelEvidenceGroups ?? []);

        return (
          <QuestionCard
            key={row.question_id}
            hasValidationError={evidenceErrorIds?.has(row.question_id) ?? false}
            assessmentId={assessmentId}
            questionId={row.question_id}
            questionKey={row.question_key ?? undefined}
            title={row.title ?? ""}
            text={renderedText}
            value={value}
            comment={comment}
            evidenceLinks={evidenceLinks}
            scaleOptions={scaleOptions}
            currentDomain={domain}
            evidenceOptions={Array.isArray(row.evidence_json) ? row.evidence_json : []}
            serviceModelEvidenceGroups={perQuestionGroups}
            question={row}
            questionNumber={resolveDisplayOrdinal(questionOrdinals, row.question_id, 'DomainSection') ?? globalIndexOffset + index + 1}
            questionnaireId={questionnaireId}
            readOnly={readOnly}
            reviewerScore={row.reviewer_score ?? null}
            reviewerNotes={row.reviewer_notes ?? null}
            onChange={(score, nextComment, links) =>
              onSave(row.question_id, score, nextComment, links)
            }
            onEvidenceUpdate={(links) => onEvidenceUpdate(row.question_id, links)}
          />
        );
      })}
      {(!rows || rows.length === 0) && (
        <div className="text-sm text-muted-foreground">No questions in {domain}.</div>
      )}
    </div>
  );
}

export default DomainSection;